/**
 * Inviting a creator to onboard (IN-1718, Flow 1 step 3).
 *
 * The workspace can add a creator by name and email, but that only creates the
 * row — the creator still has to authenticate (Instagram / YouTube) and leave
 * their payout details before they can invoice. This builds the link and the
 * message staff send them to do that.
 *
 * The link lands on the creator login rather than deep-linking to payout
 * settings, because `PrivateRoute` redirects an unauthenticated visitor to
 * /login and drops the destination. Once they are in, the invoice form's own
 * "Add your payout details first" prompt takes them the rest of the way.
 */

/** Where a creator signs in with Instagram or YouTube. */
export const CREATOR_ONBOARDING_PATH = '/login/influencer';

/** An absolute onboarding link for the host the console is being used on. */
export function onboardingLink(origin) {
  const base = String(origin || '').replace(/\/+$/, '');
  return `${base}${CREATOR_ONBOARDING_PATH}`;
}

/**
 * Whether this creator still has to onboard. `payout_details_on_file` is
 * undefined on older API builds; treat that as "nothing to chase" so staff
 * are not shown a prompt the backend cannot answer for.
 */
export function needsOnboarding(creator) {
  return (creator || {}).payout_details_on_file === false;
}

/**
 * What the roster says about this line's payout record.
 *
 * Three states, not two. Before `payout_ready` the row could only say "on
 * file" or "missing", so a creator who saved a bank account and stopped
 * before PAN read as done — and the payment run found out instead of staff.
 *
 * `payout_ready` is undefined on older API builds, and must not be read as
 * incomplete: that would mark every creator a problem the day before the
 * field ships. Same rule as `needsOnboarding` above.
 */
export function payoutState(creator) {
  const row = creator || {};
  const onFile = row.payout_details_on_file === true;

  if (!onFile) return { state: 'missing', label: 'Missing', tone: 'warn' };
  if (row.payout_ready === true) return { state: 'ready', label: 'Ready', tone: 'ok' };
  if (row.payout_ready === false) return { state: 'incomplete', label: 'Incomplete', tone: 'warn' };
  return { state: 'on_file', label: 'On file', tone: 'ok' };
}

/** A message staff can paste into WhatsApp or email, link included. */
export function onboardingMessage(creator, link) {
  const row = creator || {};
  const name = String(row.influencer_name || '').trim();
  const campaign = String(row.campaign_name || '').trim();

  const greeting = name ? `Hi ${name}` : 'Hi there';
  const forCampaign = campaign ? ` for ${campaign}` : '';

  return [
    `${greeting},`,
    '',
    `Please set up your invoicing profile${forCampaign}. It takes a minute:`,
    '',
    `1. Sign in with Instagram or YouTube: ${link}`,
    '2. Add your payout details (bank account, PAN, GST and MSME if it applies).',
    '',
    'Once that is done you can raise your invoice from the same place, and you',
    'will only have to enter these details once.',
  ].join('\n');
}
